import { useState } from 'react';
import { PhoneCall } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { InlineEdit } from '@/components/ui/InlineEdit';
import { InlineSelect } from '@/components/ui/InlineSelect';
import { PartnershipHealthBar } from '@/components/modules/PartnershipHealthBar';
import { usePartnershipsStore } from '@/stores/partnershipsStore';
import { formatCurrency, formatDate, formatRelative } from '@/lib/format';
import type { Partnership } from '@nexus/shared/types';

const tierOptions = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'standard', label: 'Standard' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'vendor', label: 'Vendor' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'churned', label: 'Churned' },
];

const tierVariant: Record<string, 'purple' | 'info' | 'success' | 'warning'> = {
  strategic: 'purple',
  standard: 'info',
  affiliate: 'success',
  vendor: 'warning',
};

interface PartnershipSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function PartnershipSheet({ id, open, onClose }: PartnershipSheetProps) {
  const { items, update, checkin } = usePartnershipsStore();
  const [checkinLoading, setCheckinLoading] = useState(false);

  const partnership: Partnership | undefined = items.find((p) => p.id === id);

  if (!partnership) {
    return (
      <Sheet open={open} onClose={onClose} title="Partnership">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const handleCheckin = async () => {
    setCheckinLoading(true);
    try { await checkin(partnership.id); } finally { setCheckinLoading(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Partnership Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <InlineEdit
            value={partnership.name}
            onSave={async (v) => { await update(partnership.id, { name: v }); }}
            className="text-lg font-semibold"
          />
          <div className="flex items-center gap-3">
            <InlineSelect
              value={partnership.tier}
              options={tierOptions}
              onSave={async (v) => { await update(partnership.id, { tier: v }); }}
            />
            <InlineSelect
              value={partnership.status}
              options={statusOptions}
              onSave={async (v) => { await update(partnership.id, { status: v }); }}
            />
          </div>
        </div>

        {/* Health Score */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Health Score</h3>
          <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
            <PartnershipHealthBar score={partnership.healthScore ?? 0} />
          </div>
        </div>

        {/* Contract Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Contract</h3>
          <div className="grid grid-cols-[120px_1fr] gap-y-3 items-center">
            <span className="text-xs text-surface-500">Value</span>
            <span className="text-sm font-medium text-surface-200">
              {partnership.contractValue != null ? formatCurrency(partnership.contractValue) : '--'}
            </span>

            <span className="text-xs text-surface-500">Commission</span>
            <span className="text-sm text-surface-300">
              {partnership.commission != null ? `${partnership.commission}%` : '--'}
            </span>

            <span className="text-xs text-surface-500">Start</span>
            <span className="text-sm text-surface-300">
              {partnership.contractStart ? formatDate(partnership.contractStart) : '--'}
            </span>

            <span className="text-xs text-surface-500">End</span>
            <span className="text-sm text-surface-300">
              {partnership.contractEnd ? formatDate(partnership.contractEnd) : '--'}
            </span>

            <span className="text-xs text-surface-500">Last Check-in</span>
            <span className="text-sm text-surface-300">
              {partnership.lastCheckinAt ? formatRelative(partnership.lastCheckinAt) : '--'}
            </span>

            <span className="text-xs text-surface-500">Next Check-in</span>
            <span className="text-sm text-surface-300">
              {partnership.nextCheckinAt ? formatDate(partnership.nextCheckinAt) : '--'}
            </span>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Notes</h3>
          <InlineEdit
            value={partnership.notes || ''}
            placeholder="Add notes..."
            onSave={async (v) => { await update(partnership.id, { notes: v }); }}
            multiline
          />
        </div>

        {/* Metadata */}
        <div className="text-xs text-surface-500">
          Updated: {formatRelative(partnership.updatedAt)}
        </div>

        {/* Actions */}
        {partnership.status === 'active' && (
          <div className="flex items-center gap-2 pt-2 border-t border-surface-700">
            <Button
              variant="primary"
              size="sm"
              icon={<PhoneCall className="h-3.5 w-3.5" />}
              onClick={handleCheckin}
              loading={checkinLoading}
            >
              Record Check-in
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
